import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';
import { createFileLogger } from '../../../../utils/logger';
import { getTemplate } from '../../Templates';

const log = createFileLogger('PLAYBOOK_COMMANDS');

export const playbookCreateCommand: SelfContainedCommand = {
  id: 'playbook_create',
  description: '⚡ CREATE: Create a new AI automation playbook',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Name for the playbook (default: "New Playbook")',
        required: false,
        defaultValue: 'New Playbook'
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'The ID of the space to create the playbook in',
        required: false
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'The ID of the folder to create the playbook in',
        required: false
      },
      {
        name: 'templateId',
        type: 'string',
        description: 'Optional template ID to seed the playbook prompt and tools. Available: "enrich-contacts"',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { title: 'M&A Deal Analysis' },
        expectedOutput: createSuccessResult('Playbook created', { entityId: 'playbook_123' }),
        description: 'Create M&A analysis playbook'
      }
    ],
    bad: []
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { title = 'New Playbook', spaceId: providedSpaceId, folderId, templateId } = params;
    const spaceId = providedSpaceId || context.spaceId;
    
    try {
      log.log('[playbook_create] Creating new playbook:', { title, spaceId, templateId });

      const template = templateId ? getTemplate(templateId) : undefined;

      const prompt = template?.prompt ?? `# Playbook Instructions

When this playbook is triggered, the AI agent should:

1. Extract information from the uploaded file
2. Research the company/topic
3. Create structured analysis documents

Be specific about:
- Which tools to use
- What outputs to create
- Where to save results`;

      const allowedTools = template?.defaultAllowedTools ?? [
        'entity_read',
        'entity_create',
        'entity_write',
        'entity_find',
        'entity_move',
        'folder_create',
        'web_search',
        'search_pdf_content',
        'api_connect',
      ];

      const triggerEntityTypes = template?.defaultTriggerEntityTypes ?? ['File.PDF'];
      
      const now = new Date().toISOString();
      
      const newPlaybookData = {
        entityType: 'Playbook',
        name: (template ? template.name : title).trim(),
        icon: '⚡',
        spaceId,
        skeleton: {
          '@type': 'Playbook',
          prompt,
          enabled: false,
          triggerEntityTypes,
          allowedTools,
          maxToolCalls: 100,
          maxDurationSeconds: 600,
          runCount: 0
        },
        created_at: now,
        ...(folderId && { groupIds: [folderId] })
      };
      
      // Use entity store actions
      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();
      
      const newPlaybook = await entityStore.actions.createEntity(newPlaybookData);
      
      log.log('[playbook_create] ✅ Playbook created:', newPlaybook._id || newPlaybook.id);
      
      const entityId = newPlaybook._id || newPlaybook.id;
      
      const resolvedTitle = (template ? template.name : title);
      return createSuccessResult(`Created playbook: ${resolvedTitle}`, {
        entityId,
        playbook: {
          id: entityId,
          name: newPlaybook.name,
          entityType: 'Playbook'
        }
      });
    } catch (error) {
      log.error('[playbook_create] Failed:', error);
      return createErrorResult(
        'Failed to create playbook',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

// Register with entity creation registry
entityCreationRegistry.register({
  commandId: 'playbook_create',
  entityType: 'Playbook',
  displayName: 'Playbook',
  icon: { type: 'phosphor', phosphorIcon: 'Lightning', phosphorWeight: 'duotone' },
  description: 'AI-driven workflow automation',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 100
});
